import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import Iconify from "../../../components/iconify";
import { useGetSitesQuery } from "../../../features/sites/sitesApiSlice";
import {
  buildPain008Xml,
  cleanIban,
  validateIBAN,
} from "../../../components/sepa/SepaUtils";

function getMemberId(m) {
  return String(m?._id || m?.id || "");
}

function getContractId(contractRef) {
  return String(
    contractRef?._id || contractRef?.id || contractRef?.contractId || "",
  );
}

function getContractName(contractRef) {
  if (!contractRef) return "";
  if (contractRef.owner) return contractRef.owner;
  const fullName = `${contractRef.title || ""} ${contractRef.firstName || ""} ${
    contractRef.lastName || ""
  }`.trim();
  return fullName;
}

function getMemberName(m) {
  return `${m?.lastName || ""} ${m?.firstName || ""}`.trim();
}

export default function FeeCollectionForm({
  members = [],
  creditor,
  loading = false,
  title = "SEPA-Lastschrift (Geb\u00fchr)",
  description =
    "W\u00e4hlen Sie Mitglieder aus und legen Sie Betrag sowie Verwendungszweck fest. Pro Vertrag wird eine Lastschrift erzeugt.",
  defaultReferenceText = "",
  defaultOnlyActive = true,
  defaultCollectionDate,
  defaultSelectedIds = [],
  defaultSelectAll = false,
  defaultFeeByMemberId = {},
  showTestingActions = false,
  onGraduateMembers,
  onDeleteTestingList,
  onExport,
}) {
  const getDefaultCollectionDate = () =>
    defaultCollectionDate || dayjs().add(2, "day").format("YYYY-MM-DD");

  const [collectionDate, setCollectionDate] = useState(
    getDefaultCollectionDate,
  );
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyActive, setOnlyActive] = useState(defaultOnlyActive);
  const [referenceText, setReferenceText] = useState(defaultReferenceText);
  const [referenceTouched, setReferenceTouched] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [feeById, setFeeById] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectionTouched, setSelectionTouched] = useState(false);
  const [graduateAfterExport, setGraduateAfterExport] = useState(false);
  const [deleteListAfterExport, setDeleteListAfterExport] = useState(false);

  const { data: sites = [], isLoading: loadingSites } = useGetSitesQuery();

  const eligibilityById = useMemo(() => {
    const map = new Map();
    members.forEach((m) => {
      const id = getMemberId(m);
      const contractRef = m?.contractRef || null;
      const iban = contractRef?.iban || "";
      const validIban = !!iban && validateIBAN(iban);
      const contractId = getContractId(contractRef);
      map.set(id, {
        isEligible: !!contractRef && !!contractId && validIban,
        hasContract: !!contractRef && !!contractId,
        validIban,
      });
    });
    return map;
  }, [members]);

  useEffect(() => {
    if (!referenceTouched && defaultReferenceText) {
      setReferenceText(defaultReferenceText);
    }
  }, [defaultReferenceText, referenceTouched]);

  useEffect(() => {
    if (selectionTouched) return;
    if (defaultSelectAll) {
      const nextIds = members
        .map((m) => getMemberId(m))
        .filter((id) => eligibilityById.get(id)?.isEligible);
      setSelectedIds(nextIds);
      return;
    }
    if (Array.isArray(defaultSelectedIds) && defaultSelectedIds.length > 0) {
      setSelectedIds(defaultSelectedIds.map((id) => String(id)).filter(Boolean));
    }
  }, [
    defaultSelectAll,
    defaultSelectedIds,
    members,
    eligibilityById,
    selectionTouched,
  ]);

  useEffect(() => {
    if (!defaultFeeByMemberId) return;
    const entries = Object.entries(defaultFeeByMemberId);
    if (!entries.length) return;
    setFeeById((prev) => {
      let changed = false;
      const next = { ...prev };
      entries.forEach(([id, fee]) => {
        if (next[id] === undefined || next[id] === null || next[id] === "") {
          const numericFee = Number(fee);
          if (Number.isFinite(numericFee)) {
            next[id] = numericFee;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [defaultFeeByMemberId]);

  const filteredMembers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return members
      .filter((m) => {
        if (!term) return true;
        return Object.values(m || {})
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(term.toLowerCase()),
          );
      })
      .filter((m) => {
        if (!selectedSiteIds.length) return true;
        const id = String(m?.standortRef?._id || m?.standortRef || "");
        return selectedSiteIds.includes(id);
      })
      .filter((m) => {
        if (!onlyActive) return true;
        return String(m?.status || "").toLowerCase() === "active";
      });
  }, [members, searchTerm, selectedSiteIds, onlyActive]);

  const selectedMembers = useMemo(() => {
    const idSet = new Set(selectedIds);
    return members.filter((m) => idSet.has(getMemberId(m)));
  }, [members, selectedIds]);

  const eligibleVisibleMembers = useMemo(
    () =>
      filteredMembers.filter((m) => {
        const info = eligibilityById.get(getMemberId(m));
        return info?.isEligible;
      }),
    [filteredMembers, eligibilityById],
  );

  const allVisibleSelected =
    eligibleVisibleMembers.length > 0 &&
    eligibleVisibleMembers.every((m) => selectedIds.includes(getMemberId(m)));
  const someVisibleSelected =
    eligibleVisibleMembers.some((m) => selectedIds.includes(getMemberId(m))) &&
    !allVisibleSelected;

  const handleToggleSelectAll = (checked) => {
    setSelectionTouched(true);
    if (checked) {
      const ids = eligibleVisibleMembers
        .map((m) => getMemberId(m))
        .filter(Boolean);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      const removeIds = new Set(
        eligibleVisibleMembers.map((m) => getMemberId(m)),
      );
      setSelectedIds((prev) => prev.filter((id) => !removeIds.has(id)));
    }
  };

  const handleToggleRow = (id) => {
    setSelectionTouched(true);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleFeeChange = (memberId, rawValue) => {
    const normalizedFee = Math.max(
      0,
      Number(String(rawValue).replace(",", ".")) || 0,
    );

    setFeeById((prev) => ({
      ...prev,
      [memberId]: normalizedFee,
    }));

    if (normalizedFee > 0 && eligibilityById.get(memberId)?.isEligible) {
      setSelectionTouched(true);
      setSelectedIds((prev) =>
        prev.includes(memberId) ? prev : [...prev, memberId],
      );
    }
  };

  const handleClearEntries = () => {
    setCollectionDate(getDefaultCollectionDate());
    setSelectedSiteIds([]);
    setSearchTerm("");
    setOnlyActive(defaultOnlyActive);
    setReferenceTouched(false);
    setReferenceText(defaultReferenceText || "");
    setSelectionTouched(true);
    setSelectedIds([]);
    setFeeById({});
    setGraduateAfterExport(false);
    setDeleteListAfterExport(false);
  };

  const rows = useMemo(() => {
    const grouped = new Map();
    selectedMembers.forEach((m) => {
      const memberId = getMemberId(m);
      const memberFee = Math.max(0, Number(feeById[memberId]) || 0);
      if (memberFee <= 0) return;
      const contractRef = m?.contractRef;
      const contractId = getContractId(contractRef);
      if (!contractRef || !contractId) return;
      const iban = contractRef?.iban || "";
      if (!validateIBAN(iban)) return;

      const existing = grouped.get(contractId) || {
        contractRef,
        members: [],
      };
      existing.members.push({ member: m, fee: memberFee });
      grouped.set(contractId, existing);
    });

    const refBase = referenceText.trim();

    return Array.from(grouped.values()).map((group) => {
      const memberNames = group.members
        .map((entry) => getMemberName(entry.member))
        .join(", ");
      const mandateDate =
        group.members
          .map((entry) =>
            entry.member?.contractDate
              ? dayjs(entry.member.contractDate).format("YYYY-MM-DD")
              : null,
          )
          .filter(Boolean)
          .sort()[0] || dayjs().format("YYYY-MM-DD");

      const mandateId =
        group.members
          .map((entry) => entry.member?.memberNumber)
          .filter(Boolean)
          .join(", ") || `VN-${getContractId(group.contractRef)}`;

      const debtorName =
        getContractName(group.contractRef) ||
        getMemberName(group.members[0]?.member);

      const remittance = (refBase || `Gebuehr ${memberNames}`)
        .trim()
        .slice(0, 140);

      const amount = group.members.reduce((sum, entry) => sum + entry.fee, 0);

      return {
        contractId: getContractId(group.contractRef),
        debtorName,
        iban: cleanIban(group.contractRef?.iban || ""),
        bic: group.contractRef?.bic || "",
        amount,
        mandateId,
        mandateDate,
        remittance,
        membersCount: group.members.length,
        memberIds: group.members.map((entry) => getMemberId(entry.member)),
      };
    });
  }, [selectedMembers, feeById, referenceText]);

  const totalAmount = useMemo(
    () => rows.reduce((sum, r) => sum + r.amount, 0),
    [rows],
  );

  const invalidSelectedCount = useMemo(() => {
    return selectedMembers.filter((m) => {
      const info = eligibilityById.get(getMemberId(m));
      return info && !info.isEligible;
    }).length;
  }, [selectedMembers, eligibilityById]);

  const zeroFeeSelectedCount = useMemo(() => {
    return selectedMembers.filter((m) => {
      const id = getMemberId(m);
      return Math.max(0, Number(feeById[id]) || 0) <= 0;
    }).length;
  }, [selectedMembers, feeById]);

  const canGenerate =
    rows.length > 0 &&
    referenceText.trim().length > 0 &&
    creditor?.creditorName &&
    creditor?.creditorId &&
    creditor?.creditorIBAN &&
    validateIBAN(creditor.creditorIBAN) &&
    /^\d{4}-\d{2}-\d{2}$/.test(collectionDate);

  const handleGenerate = async () => {
    if (!canGenerate) return;

    setIsGenerating(true);
    const startTime = performance.now();
    await new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });

    try {
      const xml = buildPain008Xml({
        creditor,
        collectionDate,
        payments: rows.map((r) => ({
          debtorName: r.debtorName,
          iban: r.iban,
          bic: r.bic,
          amount: r.amount,
          mandateId: r.mandateId,
          mandateDate: r.mandateDate,
          remittance: r.remittance,
        })),
      });

      if (!xml) {
        return;
      }

      const blob = new Blob([xml], { type: "text/xml;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const stamp = dayjs().format("YYYYMMDD-HHmmss");
      a.download = `SEPA_${stamp}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      const memberIds = rows.flatMap((r) => r.memberIds).filter(Boolean);
      const membersCount = new Set(memberIds).size;
      onExport?.(xml, {
        totalAmount,
        transactions: rows.length,
        collectionDate,
        memberIds,
        membersCount,
        referenceText: referenceText.trim(),
        siteIds: selectedSiteIds,
      });

      if (showTestingActions) {
        if (graduateAfterExport && typeof onGraduateMembers === "function") {
          await onGraduateMembers();
        }
        if (deleteListAfterExport && typeof onDeleteTestingList === "function") {
          await onDeleteTestingList();
        }
      }
    } finally {
      const elapsed = performance.now() - startTime;
      const remaining = 1000 - elapsed;
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      setIsGenerating(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        {description}
      </Typography>

      {(loading || loadingSites) && <LinearProgress sx={{ mb: 2 }} />}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          label="Fälligkeitsdatum"
          type="date"
          value={collectionDate}
          onChange={(e) => setCollectionDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Verwendungszweck"
          value={referenceText}
          onChange={(e) => {
            setReferenceTouched(true);
            setReferenceText(e.target.value);
          }}
          placeholder="z.B. Jahresgebühr 2026"
        />
        <FormControl>
          <InputLabel id="site-filter-label">Standorte</InputLabel>
          <Select
            multiple
            labelId="site-filter-label"
            label="Standorte"
            value={selectedSiteIds}
            onChange={(e) => setSelectedSiteIds(e.target.value)}
            input={<OutlinedInput label="Standorte" />}
            renderValue={(selected) => {
              if (!selected?.length) return "Alle";
              const map = new Map(sites.map((s) => [String(s._id || s.id), s]));
              return selected
                .map((id) => map.get(String(id))?.siteName || "—")
                .join(", ");
            }}
          >
            {sites.map((s) => {
              const id = String(s._id || s.id);
              return (
                <MenuItem key={id} value={id}>
                  <Checkbox checked={selectedSiteIds.indexOf(id) > -1} />
                  <Typography>{s.siteName}</Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          sx={{ minWidth: 250 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Mitglieder suchen..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  sx={{ color: "text.disabled", width: 18, height: 18 }}
                />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
            />
          }
          label="Nur aktive Mitglieder"
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      <TableContainer
        sx={{ maxHeight: 420 }}
        component={Paper}
        variant="outlined"
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  onChange={(e) => handleToggleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Mitglied</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Standort</TableCell>
              <TableCell>Kontoinhaber</TableCell>
              <TableCell>IBAN</TableCell>
              <TableCell align="right">Gebühr</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    Keine Mitglieder für die aktuelle Auswahl.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMembers.map((m) => {
                const id = getMemberId(m);
                const eligibility = eligibilityById.get(id);
                const disabled = !eligibility?.isEligible;
                const tooltipText = !eligibility?.hasContract
                  ? "Keine Vertragsdaten vorhanden"
                  : !eligibility?.validIban
                  ? "IBAN ist ungültig"
                  : "";

                return (
                  <TableRow key={id} hover>
                    <TableCell padding="checkbox">
                      <Tooltip
                        title={tooltipText}
                        arrow
                        disableHoverListener={!disabled}
                      >
                        <span>
                          <Checkbox
                            checked={selectedIds.includes(id)}
                            disabled={disabled}
                            onChange={() => handleToggleRow(id)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{getMemberName(m)}</TableCell>
                    <TableCell>{m?.status || "—"}</TableCell>
                    <TableCell>{m?.standortRef?.siteName || "—"}</TableCell>
                    <TableCell>
                      {getContractName(m?.contractRef) || "—"}
                    </TableCell>
                    <TableCell>{m?.contractRef?.iban || "—"}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={feeById[id] ?? ""}
                        onChange={(e) => handleFeeChange(id, e.target.value)}
                        placeholder="0,00"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {"\u20ac"}
                            </InputAdornment>
                          ),
                        }}
                        disabled={disabled}
                        sx={{ width: 140 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {invalidSelectedCount > 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
          {invalidSelectedCount} ausgewählte Mitglied(er) haben keine gültigen
          Vertragsdaten und werden nicht in die SEPA-Datei übernommen.
        </Typography>
      )}
      {zeroFeeSelectedCount > 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
          {zeroFeeSelectedCount} ausgewählte Mitglied(er) haben keine Gebühr
          eingetragen und werden nicht in die SEPA-Datei übernommen.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>
        Vorschau
      </Typography>
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nr.</TableCell>
              <TableCell>Debitor</TableCell>
              <TableCell align="right">Betrag</TableCell>
              <TableCell align="center">Mitglieder</TableCell>
              <TableCell>Verwendungszweck</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Noch keine SEPA-Vorschau verfügbar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, idx) => (
                <TableRow key={`${r.contractId}-${idx}`}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{r.debtorName}</TableCell>
                  <TableCell align="right">
                    {new Intl.NumberFormat("de-DE", {
                      style: "currency",
                      currency: "EUR",
                    }).format(r.amount)}
                  </TableCell>
                  <TableCell align="center">{r.membersCount}</TableCell>
                  <TableCell>{r.remittance}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Chip label={`Ausgewählt: ${selectedIds.length}`} />
        <Chip label={`Vorgänge: ${rows.length}`} />
        <Chip
          label={`Gesamt: ${new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(totalAmount)}`}
          color="secondary"
        />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        {showTestingActions && (
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={graduateAfterExport}
                  onChange={(e) => setGraduateAfterExport(e.target.checked)}
                />
              }
              label="Mitglieder graduieren"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteListAfterExport}
                  onChange={(e) => setDeleteListAfterExport(e.target.checked)}
                />
              }
              label="Prüfungsliste löschen"
            />
          </Stack>
        )}
        <Button
          variant="outlined"
          disabled={isGenerating}
          onClick={handleClearEntries}
        >
          Eingaben leeren
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!canGenerate || isGenerating}
          onClick={handleGenerate}
        >
          SEPA-Datei erzeugen
        </Button>
      </Stack>

      <Backdrop
        open={isGenerating}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
}

FeeCollectionForm.propTypes = {
  members: PropTypes.array.isRequired,
  creditor: PropTypes.shape({
    creditorName: PropTypes.string.isRequired,
    creditorIBAN: PropTypes.string.isRequired,
    creditorBIC: PropTypes.string,
    creditorId: PropTypes.string.isRequired,
    remittanceInfo: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  defaultReferenceText: PropTypes.string,
  defaultOnlyActive: PropTypes.bool,
  defaultCollectionDate: PropTypes.string,
  defaultSelectedIds: PropTypes.array,
  defaultSelectAll: PropTypes.bool,
  defaultFeeByMemberId: PropTypes.object,
  showTestingActions: PropTypes.bool,
  onGraduateMembers: PropTypes.func,
  onDeleteTestingList: PropTypes.func,
  onExport: PropTypes.func,
};
